/**
 * Auto-sort a shopping list by store aisle so the walk-through goes produce → dairy → frozen
 * in the order you actually move through the store. Pure function of the item text — no server
 * round-trip, no stored field — so it "just works" on everything already on the list.
 *
 * Method: a right-to-left head-noun heuristic ("apple juice" → juice → Beverages, "green apple"
 * → apple → Produce), with a short list of phrase overrides for the cases where the last word
 * lies ("peanut butter" is Pantry, not Dairy).
 */

export interface Aisle {
  key: string;
  label: string;
}

// Ordered the way most stores are laid out.
export const AISLES: Aisle[] = [
  { key: 'produce', label: 'Produce' },
  { key: 'bakery', label: 'Bakery' },
  { key: 'meat', label: 'Meat & Seafood' },
  { key: 'dairy', label: 'Dairy & Eggs' },
  { key: 'frozen', label: 'Frozen' },
  { key: 'pantry', label: 'Pantry' },
  { key: 'snacks', label: 'Snacks' },
  { key: 'beverages', label: 'Beverages' },
  { key: 'household', label: 'Household' },
  { key: 'personal', label: 'Personal Care' },
  { key: 'babypet', label: 'Baby & Pet' },
  { key: 'other', label: 'Other' },
];

const GROUPS: Record<string, string[]> = {
  produce: ['apple','banana','orange','lemon','lime','grape','strawberry','blueberry','raspberry','blackberry','melon','watermelon','cantaloupe','pineapple','mango','peach','pear','plum','cherry','kiwi','avocado','tomato','potato','onion','garlic','ginger','carrot','celery','lettuce','spinach','kale','arugula','cabbage','broccoli','cauliflower','cucumber','zucchini','squash','mushroom','asparagus','eggplant','cilantro','parsley','basil','mint','scallion','radish','beet','salad','greens','herbs','berries','pepper'],
  bakery: ['bread','bagel','bun','roll','croissant','muffin','tortilla','pita','baguette','loaf','cake','pie','pastry','naan','donut','biscuit'],
  meat: ['chicken','beef','steak','pork','bacon','sausage','ham','turkey','lamb','meat','fish','salmon','shrimp','crab','lobster','tilapia','cod','ribs','wings','chops','veal','brisket','tuna'],
  dairy: ['milk','cream','butter','cheese','yogurt','yoghurt','egg','cottage','mozzarella','cheddar','parmesan','feta','brie','margarine','creamer','buttermilk','ricotta','gouda'],
  frozen: ['frozen','popsicle','pizza','waffle','nuggets','fries','sorbet','gelato','tots'],
  pantry: ['pasta','spaghetti','noodle','rice','cereal','oats','oatmeal','flour','sugar','salt','oil','vinegar','sauce','ketchup','mustard','mayo','mayonnaise','honey','syrup','jam','jelly','soup','broth','stock','beans','lentils','chickpeas','salsa','spice','seasoning','yeast','cornstarch','stuffing','gravy','bouillon','breadcrumbs','taco','ramen','quinoa','couscous','canned','crackers','raisins','applesauce','pickles'],
  snacks: ['chips','cookies','candy','chocolate','popcorn','pretzels','nuts','almonds','cashews','peanuts','jerky','gum','snack','goldfish','granola'],
  beverages: ['water','juice','soda','pop','coffee','tea','wine','beer','lemonade','gatorade','seltzer','sparkling','cola','drink','kombucha','smoothie'],
  household: ['towels','tissues','napkins','foil','detergent','soap','dishwasher','bleach','cleaner','sponge','laundry','softener','batteries','lightbulb','candles','matches','ziploc','garbage','wipes','windex','lysol','swiffer','trash','plates','cups','straws','paper','toilet','tp','plastic','baggies','ziplock'],
  personal: ['shampoo','conditioner','toothpaste','toothbrush','floss','deodorant','razor','lotion','sunscreen','makeup','tampons','pads','vitamins','ibuprofen','tylenol','advil','bandaid','chapstick','mouthwash','hairspray','medicine','sunblock'],
  babypet: ['diapers','formula','litter','dog','cat','puppy','kitten','kibble','leash','baby'],
};

const WORDS = new Map<string, string>();
for (const [aisle, list] of Object.entries(GROUPS)) for (const w of list) if (!WORDS.has(w)) WORDS.set(w, aisle);

// Where the head noun lies about the aisle.
const PHRASES: Array<[string, string]> = [
  ['ice cream', 'frozen'],
  ['peanut butter', 'pantry'],
  ['almond butter', 'pantry'],
  ['black pepper', 'pantry'],
  ['bell pepper', 'produce'],
  ['green beans', 'produce'],
  ['baby wipes', 'babypet'],
  ['dish soap', 'household'],
  ['hand soap', 'personal'],
  ['bar soap', 'personal'],
  ['body wash', 'personal'],
];

const singular = (w: string): string => (w.length > 3 && w.endsWith('s') ? w.slice(0, -1) : w);

export function categorize(text: string): string {
  const n = text.toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!n) return 'other';
  for (const [phrase, aisle] of PHRASES) if (n.includes(phrase)) return aisle;
  const words = n.split(' ');
  for (let i = words.length - 1; i >= 0; i--) {
    const w = words[i]!;
    const hit = WORDS.get(w) ?? WORDS.get(singular(w)) ?? WORDS.get(w + 's');
    if (hit) return hit;
  }
  return 'other';
}

/** Bucket items into aisles, in store-walk order. Only non-empty aisles are returned. */
export function groupByAisle<T>(items: T[], getText: (t: T) => string): Array<{ aisle: Aisle; items: T[] }> {
  const buckets = new Map<string, T[]>();
  for (const it of items) {
    const k = categorize(getText(it));
    const arr = buckets.get(k);
    if (arr) arr.push(it);
    else buckets.set(k, [it]);
  }
  return AISLES.filter((a) => buckets.has(a.key)).map((a) => ({ aisle: a, items: buckets.get(a.key)! }));
}
